import React, { useEffect, useRef, useState } from 'react';

const FacilitiesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const facilities = [
    {
      name: "Seminar Hall",
      description: "State-of-the-art seminar hall equipped with modern audio-visual facilities for conferences and workshops.",
      image: "image1.png",
      color: "from-yellow-400 to-amber-500",
      category: "Seminar"
    },
    {
      name: "Zoology Lab",
      description: "Well-equipped zoology lab with specimens, microscopes, and research equipment.",
      image: "image2.png",
      color: "from-amber-500 to-orange-500",
      category: "Science"
    },
    {
      name: "Botany Lab",
      description: "Modern botany lab with plant specimens, culture rooms, and research facilities.",
      image: "image3.png",
      color: "from-green-500 to-emerald-600",
      category: "Science"
    },
    {
      name: "Physics Lab",
      description: "Advanced physics laboratory with modern equipment for experiments and research.",
      image: "image4.png",
      color: "from-blue-500 to-indigo-600",
      category: "Science"
    },
    {
      name: "Chemistry Lab",
      description: "Well-ventilated chemistry lab with safety equipment and modern apparatus.",
      image: "image5.png",
      color: "from-purple-500 to-pink-600",
      category: "Science"
    },
    {
      name: "Central Library",
      description: "Vast collection of books, journals, e-resources, and digital learning materials.",
      image: "image6.png",
      color: "from-amber-600 to-yellow-600",
      category: "Library"
    },
    {
      name: "Sick Room",
      description: "Well-maintained sick room with first-aid facilities and trained medical staff.",
      image: "image7.png",
      color: "from-red-400 to-red-600",
      category: "Health"
    },
    {
      name: "Sports Facilities",
      description: "Indoor and outdoor sports facilities including cricket, volleyball, badminton, and athletics.",
      image: "image8.png",
      color: "from-blue-600 to-cyan-600",
      category: "Sports"
    }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-b from-gr to-gray-50 py-16 overflow-hidden">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-yellow-400 rounded-full"></div>
            <div className="w-8 h-1 bg-amber-500 rounded-full"></div>
            <div className="w-6 h-1 bg-blue-600 rounded-full"></div>
          </div>

          {/* Heading */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold">
              <span className="bg-gradient-to-r from-yellow-500 via-amber-600 to-blue-700 bg-clip-text text-transparent">
                Our Facilities
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-blue-700 rounded-full mx-auto mt-3"></div>
            <p className="text-gray-600 text-sm mt-4">
              State-of-the-art Infrastructure for Holistic Learning
            </p>
          </div>

          {/* First Row - 4 Items */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {facilities.slice(0, 4).map((item, idx) => (
              <div
                key={idx}
                className={`group bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-500 transform hover:shadow-2xl hover:-translate-y-2 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                {/* Image Container */}
                <div className="relative overflow-hidden h-48">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${item.color} opacity-0 group-hover:opacity-30 transition-opacity duration-300`}></div>
                  <div className="absolute top-2 right-2">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full bg-white/90 text-gray-700`}>
                      {item.category}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-white text-xs">{item.description.substring(0, 60)}...</p>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-800 text-sm">{item.name}</h4>
                    <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${item.color} animate-pulse`}></div>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    {item.description}
                  </p>
                  
                  {/* Learn More Link */}
                  <button className="mt-3 text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-amber-500 hover:to-yellow-500 transition-all duration-300 flex items-center gap-1 group/btn">
                    Learn More 
                    <span className="inline-block transition-transform duration-300 group-hover/btn:translate-x-1">→</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Second Row - 4 Items */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {facilities.slice(4, 8).map((item, idx) => (
              <div
                key={idx}
                className={`group bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-500 transform hover:shadow-2xl hover:-translate-y-2 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${(idx + 4) * 100}ms` }}
              >
                {/* Image Container */}
                <div className="relative overflow-hidden h-48">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${item.color} opacity-0 group-hover:opacity-30 transition-opacity duration-300`}></div>
                  <div className="absolute top-2 right-2">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full bg-white/90 text-gray-700`}>
                      {item.category}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-white text-xs">{item.description.substring(0, 60)}...</p>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-800 text-sm">{item.name}</h4>
                    <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${item.color} animate-pulse`}></div>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    {item.description}
                  </p>
                  
                  {/* Learn More Link */}
                  <button className="mt-3 text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-amber-500 hover:to-yellow-500 transition-all duration-300 flex items-center gap-1 group/btn">
                    Learn More 
                    <span className="inline-block transition-transform duration-300 group-hover/btn:translate-x-1">→</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-1.5">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default FacilitiesSection;